import type { MfaStage } from '@/stores/auth'

/**
 * One function decides every navigation, so the order of the checks is visible
 * in one place rather than spread over three hooks. It is pure: the router
 * hook reads the store and hands the answer in, which is also what makes each
 * rule testable without mounting a router.
 */

export interface RouteMeta {
  /** Reachable with no token at all, such as sign-in or password reset. */
  public?: boolean
  /** Part of the multi-factor flow; reachable with a pre-MFA token only. */
  stage?: 'mfa'
  /** Permission string the user must hold, as `/auth/me` spells it. */
  permission?: string
}

export interface RouteIntent {
  name: string
  fullPath: string
  meta: RouteMeta
}

export interface SessionView {
  isAuthenticated: boolean
  mfaStage: MfaStage | null
  can: (permission: string) => boolean
}

export type Redirect = { name: string; query?: Record<string, string> }

const HOME = 'dashboard'
const SIGN_IN = 'sign-in'
const FORBIDDEN = 'forbidden'

const MFA_PAGE: Record<Exclude<MfaStage, 'verified'>, string> = {
  setup_required: 'mfa-setup',
  verify_required: 'mfa-verify',
}

export function resolveNavigation(to: RouteIntent, session: SessionView): true | Redirect {
  const pending = session.mfaStage === 'setup_required' || session.mfaStage === 'verify_required'

  // A half-authenticated token holds the MFA pages and nothing else. This is
  // checked before `public` so a pre-MFA user cannot wander back to sign-in
  // and leave a live token behind.
  if (pending) {
    if (to.meta.stage === 'mfa') return true
    return { name: MFA_PAGE[session.mfaStage as Exclude<MfaStage, 'verified'>] }
  }

  if (session.isAuthenticated) {
    // Sign-in and the MFA pages are finished business for a live session.
    if (to.meta.public === true || to.meta.stage === 'mfa') return { name: HOME }
    if (to.meta.permission !== undefined && !session.can(to.meta.permission)) {
      return { name: FORBIDDEN }
    }
    return true
  }

  if (to.meta.public === true) return true

  // Carry the whole path, query included, so the user lands where they aimed.
  return { name: SIGN_IN, query: { redirect: to.fullPath } }
}

/**
 * Narrows the `redirect` query parameter to a path this app could have written.
 *
 * The value reaches the sign-in page from the address bar, so a crafted link
 * could otherwise bounce a freshly authenticated user to another origin. Only
 * a single leading slash is a path: `//host` and `/\\host` are both read as
 * another origin by at least one browser.
 */
export function safeRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//') || value.startsWith('/\\')) return null
  if (value.includes('\\')) return null
  return value
}
